/*
 * NASTY TV product block: two independent jobs on the same custom element.
 *
 * 1. Swaps a cutdown into the player in place, per the deployment brief's own build note -- a tap
 *    changes what is playing, it never opens a new page. Every slide is already in the DOM, so the
 *    swap is just moving `hidden` between slides; the newly-hidden slide's video is paused so it
 *    does not keep playing off-screen.
 *
 * 2. Builds a small custom control bar -- scrub with a buffered range, time, fullscreen, a
 *    click-anywhere toggle and a centre icon that flashes on state change -- for every video that
 *    appears anywhere inside this element, deliberately without a volume control. This exists
 *    because native video controls render inside a closed UA shadow root: a click anywhere in that
 *    root, including on the browser's own pause button, gets retargeted so it looks identical to a
 *    click on the `<video>` element once it bubbles out. A hand-rolled click-to-toggle listener run
 *    alongside native controls cannot tell the two apart, so it either fights them or has to
 *    replace them -- tried both, in that order, on this exact video. Replacing them is the one that
 *    actually works, so `enhanceVideo` strips `controls` from anything it finds (a Shopify-picked
 *    video from `render 'video'` defaults to native controls of its own) and builds this instead.
 *
 * A MutationObserver covers videos that only appear after a poster click: `snippets/video.liquid`'s
 * `<deferred-media>` keeps its real `<video>` inert inside a `<template>` until someone taps the
 * poster, so nothing worth enhancing exists in the DOM yet at first paint for a posted slide.
 *
 * Progressive enhancement: without this script every slide is still in the document and the
 * long-form is the one left visible, so a no-JS visit still gets a working, playable long-form
 * video -- just with whatever controls its own markup shipped with (native, for a Shopify-picked
 * video; none at all for the URL fallback, which has no no-JS play path either way).
 */

const PLAY_SVG =
  '<svg viewBox="0 0 20 20" fill="none" aria-hidden="true"><path fill-rule="evenodd" clip-rule="evenodd" d="M15.375 8.48516C16.5417 9.15874 16.5417 10.8427 15.375 11.5163L7.875 15.8464C6.70833 16.52 5.25 15.678 5.25 14.3308L5.25 5.67058C5.25 4.32343 6.70833 3.48146 7.875 4.15504L15.375 8.48516Z" fill="currentColor"/></svg>';

const PAUSE_SVG =
  '<svg viewBox="0 0 20 20" fill="none" aria-hidden="true"><path d="M12.5 5v10" stroke="currentColor" stroke-width="2" stroke-linecap="round"/><path d="M7.5 5v10" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>';

const FULLSCREEN_ENTER_SVG =
  '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M9 3H5a2 2 0 0 0-2 2v4M15 3h4a2 2 0 0 1 2 2v4M9 21H5a2 2 0 0 1-2-2v-4M15 21h4a2 2 0 0 0 2-2v-4" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';

const FULLSCREEN_EXIT_SVG =
  '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M4 14h4a2 2 0 0 1 2 2v4M20 14h-4a2 2 0 0 0-2 2v4M4 10h4a2 2 0 0 0 2-2V4M20 10h-4a2 2 0 0 1-2-2V4" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';

const HIDE_AFTER_MS = 2500;

/*
 * How long a wait has to last before the spinner appears.
 *
 * Not zero. A clip that is already buffered answers a seek in well under a frame, and a spinner
 * that appears and vanishes inside 80ms reads as a glitch rather than as feedback -- it draws the
 * eye to a wait that never happened. 200ms is the usual floor for "long enough that a person has
 * started to wonder", so the indicator only ever shows up when there is something to wait for.
 */
const BUFFER_DELAY_MS = 200;

/** Pending show-spinner timers, keyed by the <deferred-media> that owns them. */
const bufferTimers = new WeakMap();

/**
 * Arms the spinner for a container. Idempotent: a video that fires `waiting` and `seeking` back to
 * back must not start two timers, or the second leaks and the class never clears.
 * @param {Element | null} container
 */
function scheduleBuffering(container) {
  if (!container || bufferTimers.has(container)) return;
  const timer = setTimeout(() => {
    bufferTimers.delete(container);
    container.classList.add('is-buffering');
    container.setAttribute('aria-busy', 'true');
  }, BUFFER_DELAY_MS);
  bufferTimers.set(container, timer);
}

/**
 * Clears the spinner, whether it was showing or still only armed.
 * @param {Element | null} container
 */
function clearBuffering(container) {
  if (!container) return;
  const timer = bufferTimers.get(container);
  if (timer) {
    clearTimeout(timer);
    bufferTimers.delete(container);
  }
  container.classList.remove('is-buffering');
  container.removeAttribute('aria-busy');
}

function formatTime(rawSeconds) {
  const seconds = Number.isFinite(rawSeconds) && rawSeconds > 0 ? Math.floor(rawSeconds) : 0;
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  const ss = String(s).padStart(2, '0');
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${ss}`;
  return `${m}:${ss}`;
}

/** @param {HTMLVideoElement} video */
function enhanceVideo(video) {
  if (!(video instanceof HTMLVideoElement) || video.dataset.tvControls) return;
  video.dataset.tvControls = 'true';
  video.removeAttribute('controls');

  const container = video.parentElement;
  if (!container) return;

  const controls = document.createElement('div');
  controls.className = 'nasty-g-tv-product__controls';
  controls.innerHTML = `
    <div class="nasty-g-tv-product__center" aria-hidden="true">
      <span class="nasty-g-tv-product__center-icon nasty-g-tv-product__center-icon--play">${PLAY_SVG}</span>
      <span class="nasty-g-tv-product__center-icon nasty-g-tv-product__center-icon--pause">${PAUSE_SVG}</span>
    </div>
    <div class="nasty-g-tv-product__spinner" aria-hidden="true"></div>
    <div class="nasty-g-tv-product__bar">
      <div
        class="nasty-g-tv-product__scrub"
        role="slider"
        tabindex="0"
        aria-label="Seek"
        aria-valuemin="0"
        aria-valuemax="100"
        aria-valuenow="0"
      >
        <div class="nasty-g-tv-product__scrub-track">
          <div class="nasty-g-tv-product__scrub-buffered"></div>
          <div class="nasty-g-tv-product__scrub-played"></div>
          <div class="nasty-g-tv-product__scrub-thumb"></div>
        </div>
      </div>
      <div class="nasty-g-tv-product__time">
        <span class="nasty-g-tv-product__time-current">0:00</span><span class="nasty-g-tv-product__time-sep">/</span><span class="nasty-g-tv-product__time-duration">0:00</span>
      </div>
      <button type="button" class="nasty-g-tv-product__fullscreen" aria-label="Fullscreen">
        <span class="nasty-g-tv-product__fullscreen-icon nasty-g-tv-product__fullscreen-icon--enter">${FULLSCREEN_ENTER_SVG}</span>
        <span class="nasty-g-tv-product__fullscreen-icon nasty-g-tv-product__fullscreen-icon--exit">${FULLSCREEN_EXIT_SVG}</span>
      </button>
    </div>
  `;
  container.appendChild(controls);

  /*
   * The wait states. `waiting` and `stalled` cover a mid-playback buffer; `seeking` covers a scrub
   * that lands outside what is buffered, which on these files is most of them. `pause` and `error`
   * clear it as well as the success events -- a spinner left turning over a paused or broken player
   * is worse than none, because it claims progress that is not happening.
   */
  for (const event of ['waiting', 'seeking', 'stalled']) {
    video.addEventListener(event, () => scheduleBuffering(container));
  }
  for (const event of ['playing', 'seeked', 'canplay', 'pause', 'ended', 'error']) {
    video.addEventListener(event, () => clearBuffering(container));
  }

  const center = controls.querySelector('.nasty-g-tv-product__center');
  const scrub = controls.querySelector('.nasty-g-tv-product__scrub');
  const playedBar = controls.querySelector('.nasty-g-tv-product__scrub-played');
  const bufferedBar = controls.querySelector('.nasty-g-tv-product__scrub-buffered');
  const thumb = controls.querySelector('.nasty-g-tv-product__scrub-thumb');
  const currentEl = controls.querySelector('.nasty-g-tv-product__time-current');
  const durationEl = controls.querySelector('.nasty-g-tv-product__time-duration');
  const fullscreenButton = controls.querySelector('.nasty-g-tv-product__fullscreen');

  let hideTimer;
  const showBar = () => {
    controls.classList.add('is-visible');
    clearTimeout(hideTimer);
    if (!video.paused) {
      hideTimer = setTimeout(() => controls.classList.remove('is-visible'), HIDE_AFTER_MS);
    }
  };

  const flashCenter = (playing) => {
    center.classList.toggle('is-play', playing);
    center.classList.toggle('is-pause', !playing);
    // Restart the fade animation even if it's already mid-run from a fast double toggle.
    center.classList.remove('is-flashing');
    void center.offsetWidth;
    center.classList.add('is-flashing');
  };

  video.addEventListener('click', () => {
    if (video.paused) {
      video.play();
    } else {
      video.pause();
    }
  });

  video.addEventListener('play', () => {
    controls.classList.add('is-playing');
    flashCenter(true);
    showBar();
  });

  video.addEventListener('pause', () => {
    controls.classList.remove('is-playing');
    flashCenter(false);
    clearTimeout(hideTimer);
    controls.classList.add('is-visible');
  });

  video.addEventListener('loadedmetadata', () => {
    durationEl.textContent = formatTime(video.duration);
  });

  video.addEventListener('timeupdate', () => {
    if (!video.duration || dragging) return;
    const ratio = video.currentTime / video.duration;
    playedBar.style.width = `${ratio * 100}%`;
    thumb.style.insetInlineStart = `${ratio * 100}%`;
    currentEl.textContent = formatTime(video.currentTime);
    scrub.setAttribute('aria-valuenow', String(Math.round(ratio * 100)));
  });

  video.addEventListener('progress', () => {
    if (!video.duration || video.buffered.length === 0) return;
    const end = video.buffered.end(video.buffered.length - 1);
    bufferedBar.style.width = `${Math.min(end / video.duration, 1) * 100}%`;
  });

  const ratioFromEvent = (event) => {
    const rect = scrub.getBoundingClientRect();
    if (!rect.width) return null;
    return Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width));
  };

  const paintProgress = (ratio) => {
    playedBar.style.width = `${ratio * 100}%`;
    thumb.style.insetInlineStart = `${ratio * 100}%`;
    currentEl.textContent = formatTime(ratio * video.duration);
    scrub.setAttribute('aria-valuenow', String(Math.round(ratio * 100)));
  };

  /*
   * Scrubbing previews on drag and seeks once on release, the way YouTube's bar does. Seeking on
   * every pointermove fired dozens of seeks a second, and each one can make the browser drop its
   * current fetch and start a new byte-range request to the CDN. On these files, whose index sits
   * at the end, that churn could stall playback or drop it back to the start. `timeupdate` is held
   * off while dragging so the playing position doesn't snap the thumb back under the finger.
   */
  let dragging = false;
  let dragRatio = null;

  scrub.addEventListener('pointerdown', (event) => {
    if (!Number.isFinite(video.duration) || video.duration <= 0) return;
    dragging = true;
    scrub.setPointerCapture(event.pointerId);
    dragRatio = ratioFromEvent(event);
    if (dragRatio !== null) paintProgress(dragRatio);
    controls.classList.add('is-scrubbing');
    showBar();
  });

  scrub.addEventListener('pointermove', (event) => {
    if (!dragging) return;
    dragRatio = ratioFromEvent(event);
    if (dragRatio !== null) paintProgress(dragRatio);
    showBar();
  });

  /*
   * `Number.isFinite`, not a truthiness check. A progressive file that is still resolving its
   * header can report `duration` as `Infinity` for the first moments after play, and `Infinity`
   * passes `if (video.duration)`. `dragRatio * Infinity` is `Infinity` (or `NaN` at ratio 0), and
   * assigning either to `currentTime` is an invalid seek that a browser may answer by resetting the
   * element to the start -- the same symptom as the seek this whole block exists to make reliable.
   */
  const endDrag = (commit) => {
    if (!dragging) return;
    dragging = false;
    controls.classList.remove('is-scrubbing');
    if (commit && dragRatio !== null && Number.isFinite(video.duration) && video.duration > 0) {
      video.currentTime = dragRatio * video.duration;
    }
    dragRatio = null;
  };

  scrub.addEventListener('pointerup', () => endDrag(true));
  scrub.addEventListener('pointercancel', () => endDrag(false));
  scrub.addEventListener('lostpointercapture', () => endDrag(true));

  scrub.addEventListener('keydown', (event) => {
    if (!Number.isFinite(video.duration) || video.duration <= 0) return;
    if (event.key === 'Home') {
      video.currentTime = 0;
    } else if (event.key === 'End') {
      video.currentTime = video.duration;
    } else if (event.key === 'ArrowRight' || event.key === 'ArrowUp') {
      video.currentTime = Math.min(video.duration, video.currentTime + 5);
    } else if (event.key === 'ArrowLeft' || event.key === 'ArrowDown') {
      video.currentTime = Math.max(0, video.currentTime - 5);
    } else {
      return;
    }
    event.preventDefault();
    showBar();
  });

  fullscreenButton.addEventListener('click', (event) => {
    event.stopPropagation();
    if (document.fullscreenElement === container) {
      document.exitFullscreen();
    } else if (container.requestFullscreen) {
      container.requestFullscreen().then(() => {
        // A phone held upright makes the fullscreen box portrait. Where the browser allows it
        // (Android Chrome), turn a landscape video sideways so it fills the screen.
        if (video.videoWidth > video.videoHeight) {
          screen.orientation?.lock?.('landscape').catch(() => {});
        }
      }, () => {});
    } else if (video.webkitEnterFullscreen) {
      // iOS Safari has no element Fullscreen API; the video's own fullscreen player is the
      // closest equivalent and brings its own native controls back only while active.
      video.webkitEnterFullscreen();
    }
  });

  document.addEventListener('fullscreenchange', () => {
    controls.classList.toggle('is-fullscreen', document.fullscreenElement === container);
    if (!document.fullscreenElement) screen.orientation?.unlock?.();
  });

  container.addEventListener('pointermove', showBar);
  container.addEventListener('pointerdown', showBar);

  showBar();
}

function enhanceWithin(root) {
  if (root.tagName === 'VIDEO') enhanceVideo(root);
  root.querySelectorAll?.('video').forEach(enhanceVideo);
}

class NastyTvPlayer extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.#onClick);

    enhanceWithin(this);
    this.#observer = new MutationObserver(this.#onMutate);
    this.#observer.observe(this, { childList: true, subtree: true });

    this.#syncCutsHeight();
    this.#resizeObserver = new ResizeObserver(this.#syncCutsHeight);
    this.querySelectorAll('.nasty-g-tv-product__frame').forEach((frame) => {
      this.#resizeObserver.observe(frame);
    });

    this.#revealCurrentTile();
  }

  /**
   * The player can open on a clip further down the posting order, the first one with a video. On
   * the phone swipe row that tile can start off-screen, so the row is scrolled to it on load. Only
   * the row's own `scrollLeft` moves, never the page. On desktop the column has no horizontal
   * overflow, so this does nothing there.
   */
  #revealCurrentTile() {
    const list = this.querySelector('.nasty-g-tv-product__cuts-list');
    const current = list?.querySelector('[aria-current="true"]')?.closest('li');
    if (!list || !current || list.scrollWidth <= list.clientWidth) return;
    const padding = parseFloat(getComputedStyle(list).scrollPaddingInlineStart) || 0;
    list.scrollLeft = current.offsetLeft - list.offsetLeft - padding;
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#resizeObserver?.disconnect();
  }

  /** @type {MutationObserver | undefined} */
  #observer;

  /** @type {ResizeObserver | undefined} */
  #resizeObserver;

  #syncCutsHeight = () => {
    const visibleFrame = this.querySelector('[data-tv-slide]:not([hidden]) .nasty-g-tv-product__frame');
    if (!(visibleFrame instanceof HTMLElement)) return;
    this.style.setProperty('--tv-frame-height', `${visibleFrame.offsetHeight}px`);
  };

  #onMutate = (mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (node.nodeType !== Node.ELEMENT_NODE) continue;
        enhanceWithin(/** @type {Element} */ (node));
      }
    }
  };

  #onClick = (event) => {
    /*
     * The first wait starts here, not on a video event: between this click and the cloned <video>
     * being inserted there is no element to listen to yet, and that gap is the longest wait on the
     * page -- the file has not been touched at all until now. `enhanceVideo` takes over from the
     * media events once the node exists, and both write the same class to the same container.
     */
    const poster = event.target.closest('.deferred-media__poster-button');
    if (poster && this.contains(poster)) {
      scheduleBuffering(poster.closest('deferred-media'));
    }

    const tile = event.target.closest('[data-tv-target]');
    if (!tile || !this.contains(tile)) return;

    const targetId = tile.getAttribute('data-tv-target');
    const target = this.querySelector(`#${CSS.escape(targetId)}`);
    if (!target) return;

    for (const slide of this.querySelectorAll('[data-tv-slide]')) {
      if (slide === target) continue;
      slide.hidden = true;
      slide.querySelector('video')?.pause();
    }
    target.hidden = false;
    this.#syncCutsHeight();

    // Only row tiles carry the "now playing" state. The way-back button inside a short cut's
    // caption targets the feature, which has no tile, so it clears every tile instead.
    for (const button of this.querySelectorAll('.nasty-g-tv-product__tile[data-tv-target]')) {
      if (button === tile) {
        button.setAttribute('aria-current', 'true');
      } else {
        button.removeAttribute('aria-current');
      }
    }

    // A control inside a slide just hid itself along with that slide, which would drop keyboard
    // focus to the top of the document. Hand it to the newly shown player instead.
    if (tile.closest('[data-tv-slide]')) {
      target.querySelector('button, video, a[href], [tabindex]:not([tabindex="-1"])')?.focus({ preventScroll: true });
    }
  };
}

if (!customElements.get('nasty-tv-player')) {
  customElements.define('nasty-tv-player', NastyTvPlayer);
}
